import { Component, Inject, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { TICKET_PRIORITIES, TicketPriority, ticketPriority, SupportTicket, TICKET_TYPES, TicketType, HARDWARE_TOOLS } from '@shared/models/entities/supportTicket.model';

/** Módulo seleccionable en el formulario. */
export interface TicketModuleOption {
  id:    string | null;
  label: string;
}

export interface TicketFormDialogData {
  modules: TicketModuleOption[];
  /** Se muestra como "Fecha de creación" (solo lectura). */
  createdOn: string;
  /** Nombre del usuario que levanta el ticket (solo lectura). */
  requestedByName: string;
  /** Si viene, el diálogo EDITA este ticket (lo usa quien lo levantó). */
  ticket?: SupportTicket;
}

/** Lo que devuelve el diálogo al guardar. */
export interface TicketFormResult {
  ticketType:  TicketType;
  priority:    TicketPriority;
  subject:     string;
  moduleId:    string | null;
  module:      string;
  location:    string;
  description: string;
}

/**
 * Alta de un ticket de soporte.
 *
 * No usa `SimpleFormDialogComponent` porque ese diálogo llama al servicio él
 * mismo (`service.create(...)`) y aquí el alta tiene un paso extra: después
 * de guardar hay que mandar el correo a la mesa de servicio. Este diálogo
 * solo captura y devuelve los datos; quien orquesta es la pantalla.
 */
@Component({
  selector: 'app-ticket-form-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MaterialModule],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>{{ data.ticket ? 'edit' : 'confirmation_number' }}</mat-icon>
      {{ data.ticket ? 'Editar ticket ' + data.ticket.folio : 'Nuevo ticket de soporte' }}
    </h2>

    <mat-dialog-content class="mat-typography">

      <!-- Datos que no se capturan: se muestran para que el usuario sepa
           con qué se va a registrar el ticket. -->
      <div class="readonly-row">
        <div class="readonly-item">
          <span class="readonly-label">Fecha de creación</span>
          <span class="readonly-value">{{ data.createdOn }}</span>
        </div>
        <div class="readonly-item">
          <span class="readonly-label">Levantado por</span>
          <span class="readonly-value">{{ data.requestedByName }}</span>
        </div>
      </div>

      <form [formGroup]="form" class="ticket-form">

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Asunto</mat-label>
          <input matInput formControlName="subject" maxlength="120"
                 placeholder="Resume el problema en una línea">
          <mat-hint align="end">{{ form.get('subject')?.value?.length ?? 0 }}/120</mat-hint>
          @if (form.get('subject')?.touched && form.get('subject')?.invalid) {<mat-error>El asunto es obligatorio.</mat-error>}
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Tipo de ticket</mat-label>
          <mat-select formControlName="ticketType">
            @for (t of ticketTypes; track t.id) {
              <mat-option [value]="t.id">{{ t.name }}</mat-option>
            }
          </mat-select>
          @if (form.get('ticketType')?.touched && form.get('ticketType')?.invalid) {<mat-error>Elige si es software o hardware.</mat-error>}
        </mat-form-field>

        @if (form.get('ticketType')?.value === 'SOFTWARE') {
          <!-- Solo los módulos que tiene el rol de quien levanta el ticket. -->
          <mat-form-field appearance="outline">
            <mat-label>Módulo</mat-label>
            <mat-select formControlName="moduleId">
              @for (option of data.modules; track option.label) {
                <mat-option [value]="option.id ?? option.label">{{ option.label }}</mat-option>
              }
            </mat-select>
            @if (form.get('moduleId')?.touched && form.get('moduleId')?.invalid) {<mat-error>Selecciona el módulo donde ocurre.</mat-error>}
          </mat-form-field>
        } @else if (form.get('ticketType')?.value === 'HARDWARE') {
          <mat-form-field appearance="outline">
            <mat-label>Herramienta</mat-label>
            <mat-select formControlName="tool">
              @for (tool of hardwareTools; track tool) {
                <mat-option [value]="tool">{{ tool }}</mat-option>
              }
            </mat-select>
            @if (form.get('tool')?.touched && form.get('tool')?.invalid) {<mat-error>Selecciona la herramienta.</mat-error>}
          </mat-form-field>
        }

        <mat-form-field appearance="outline">
          <mat-label>¿En qué parte?</mat-label>
          <input matInput formControlName="location" maxlength="120"
                 placeholder="Pantalla, botón, tabla, reporte…">
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Prioridad</mat-label>
          <mat-select formControlName="priority">
            @for (p of priorities; track p.id) {
              <mat-option [value]="p.id"><b>{{ p.name }}</b> — {{ p.description }}</mat-option>
            }
          </mat-select>
          @if (selectedPriority; as sp) {<mat-hint>Respuesta: {{ sp.response }} · Tiempo estimado (resolución): {{ sp.resolution }}</mat-hint>}
          @if (form.get('priority')?.touched && form.get('priority')?.invalid) {<mat-error>Elige la prioridad.</mat-error>}
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Descripción</mat-label>
          <textarea matInput formControlName="description" rows="5"
                    placeholder="Qué esperabas que pasara, qué pasó y cómo reproducirlo"></textarea>
          @if (form.get('description')?.touched && form.get('description')?.invalid) {<mat-error>La descripción es obligatoria.</mat-error>}
        </mat-form-field>

      </form>

      @if (!data.ticket) {
        <p class="mail-note">
          <mat-icon>mail_outline</mat-icon>
          Al guardar se envía un correo a la mesa de servicio.
        </p>
      }

    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-raised-button mat-dialog-close [disabled]="saving()">Cancelar</button>
      <button mat-raised-button color="accent" (click)="save()" [disabled]="saving()">
        @if (saving()) {
          <mat-spinner diameter="18"></mat-spinner>
        } @else {
          {{ data.ticket ? 'Guardar cambios' : 'Levantar ticket' }}
        }
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 { display: flex; align-items: center; gap: 8px; color: #170f5f; font-weight: bold; }
    .readonly-row {
      display: flex; gap: 24px; flex-wrap: wrap;
      background: #dde7ff; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px;
    }
    .readonly-item { display: flex; flex-direction: column; }
    .readonly-label { font-size: 11px; font-weight: 700; color: #1010eb; text-transform: uppercase; letter-spacing: .4px; }
    .readonly-value { font-size: 14px; color: #170f5f; }
    .ticket-form { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; }
    .ticket-form mat-form-field { width: 100%; }
    .full-col { grid-column: 1 / -1; }
    .mail-note {
      display: flex; align-items: center; gap: 6px;
      font-size: 12px; color: #555555; margin: 4px 0 0;
    }
    .mail-note mat-icon { font-size: 16px; width: 16px; height: 16px; }
    @media (max-width: 600px) { .ticket-form { grid-template-columns: 1fr; } }
  `],
})
export class TicketFormDialogComponent {

  private formBuilder = inject(FormBuilder);

  readonly saving = signal(false);

  readonly priorities = TICKET_PRIORITIES;
  get selectedPriority() { return ticketPriority(this.form.get('priority')?.value); }

  readonly ticketTypes = TICKET_TYPES;
  readonly hardwareTools = HARDWARE_TOOLS;

  form: FormGroup = this.formBuilder.group({
    priority:    [null, Validators.required],
    subject:     [null, Validators.required],
    ticketType:  [null, Validators.required],
    moduleId:    [null],
    tool:        [null],
    location:    [null],
    description: [null, Validators.required],
  });

  constructor(
    public dialogRef: MatDialogRef<TicketFormDialogComponent, TicketFormResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: TicketFormDialogData,
  ) {
    // Software → se exige módulo; hardware → se exige herramienta.
    this.form.get('ticketType')?.valueChanges.subscribe((type: TicketType) => {
      const moduleCtrl = this.form.get('moduleId')!;
      const toolCtrl = this.form.get('tool')!;
      moduleCtrl.setValidators(type === 'SOFTWARE' ? [Validators.required] : []);
      toolCtrl.setValidators(type === 'HARDWARE' ? [Validators.required] : []);
      if (type !== 'SOFTWARE') moduleCtrl.setValue(null, { emitEvent: false });
      if (type !== 'HARDWARE') toolCtrl.setValue(null, { emitEvent: false });
      moduleCtrl.updateValueAndValidity({ emitEvent: false });
      toolCtrl.updateValueAndValidity({ emitEvent: false });
    });
    const t = data.ticket;
    if (t) {
      // El select usa el id del módulo o, en la lista de respaldo, su etiqueta.
      const moduleValue = t.moduleId ?? (data.modules.find(m => m.label === t.module)?.label ?? t.module);
      const isHardware = t.ticketType === 'HARDWARE' || HARDWARE_TOOLS.includes(t.module);
      this.form.patchValue({ ticketType: isHardware ? 'HARDWARE' : 'SOFTWARE' });
      this.form.patchValue({
        priority:    t.priority ?? null,
        subject:     t.subject,
        moduleId:    isHardware ? null : moduleValue,
        tool:        isHardware ? t.module : null,
        location:    t.location ?? null,
        description: t.description,
      });
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;

    // El value del select es el id del módulo cuando existe en BD, o su
    // etiqueta cuando venimos de la lista de respaldo (sin tabla `modules`
    // sembrada). Se resuelve aquí para guardar siempre ambos.
    const isHardware = value.ticketType === 'HARDWARE';
    const selected = isHardware ? undefined : this.data.modules.find(
      m => (m.id ?? m.label) === value.moduleId
    );

    this.dialogRef.close({
      ticketType:  value.ticketType as TicketType,
      priority:    value.priority as TicketPriority,
      subject:     String(value.subject).trim(),
      // Hardware: la herramienta se guarda en `module` (sin id de módulo).
      moduleId:    isHardware ? null : (selected?.id ?? null),
      module:      isHardware ? String(value.tool) : (selected?.label ?? String(value.moduleId)),
      location:    value.location ? String(value.location).trim() : '',
      description: String(value.description).trim(),
    });
  }
}
